// src/pages/user/DepositFunds.jsx
import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import StickyFooter from '../components/StickyFooter';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Button,
  Grid,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import PaymentIcon from '@mui/icons-material/Payment';
import { motion } from 'framer-motion';

const depositMethods = [
  { label: 'Credit Card', icon: <CreditCardIcon sx={{ color: '#7B2FF2' }} />, value: 'credit' },
  { label: 'Bank Transfer', icon: <AccountBalanceIcon sx={{ color: '#0ea5e9' }} />, value: 'bank' },
  { label: 'Bitcoin', icon: <CurrencyBitcoinIcon sx={{ color: '#f59e0b' }} />, value: 'bitcoin' },
];

const presetAmounts = [100, 500, 1000, 5000, 10000];

const DepositFunds = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [method, setMethod] = useState('bank');
  const [amount, setAmount] = useState(100);
  const [openModal, setOpenModal] = useState(false);
  const [timer, setTimer] = useState(1800); // 30 min in seconds

  // Countdown
  useEffect(() => {
    let interval;
    if (openModal) {
      interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [openModal]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const renderMethodDetails = () => {
    switch (method) {
      case 'bank':
        return (
          <>
            <Row label="Bank Name" value="GTBank" />
            <Row label="Account Number" value="1234567890" />
            <Row label="Account Name" value="ViteCoin Global" />
          </>
        );
      case 'credit':
        return (
          <>
            <Row label="Supported Cards" value="Visa, Mastercard, Verve" />
            <Row label="Processing" value="Secure Payment Gateway" />
          </>
        );
      case 'bitcoin':
        return (
          <>
            <Row
              label="BTC Address"
              value="1FfmbHfnpaZjKFvyi1okTjJJusN455paPH"
              isCrypto
            />
          </>
        );
      default:
        return null;
    }
  };

  const Row = ({ label, value, isCrypto }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={1}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          wordBreak: isCrypto ? 'break-all' : 'normal',
          color: isCrypto ? '#f59e0b' : 'text.primary',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const handleProceed = () => {
    setTimer(1800); // reset timer each time
    setOpenModal(true);
  };

  const handleMenuClick = () => {
    console.log('Menu clicked!');
  };

  return (
    <>
      <Header mode={mode} onMenuClick={handleMenuClick} />
      <Box p={{ xs: 2, marginTop: 70, md: 4 }}>
        {/* Breadcrumb */}
        <Typography variant="body2" color="text.secondary" fontSize={12} mb={1}>
          Dashboard &gt; Deposit &gt; Make Deposit
        </Typography>

        {/* Page Title */}
        <Typography variant="h6" fontWeight={600} mb={3}>
          Deposit Funds
        </Typography>

        {/* Gradient Header Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Paper
            sx={{
              borderRadius: 3,
              color: '#fff',
              background: 'linear-gradient(to bottom, #3AB7F9, #0164C1)',
              p: 3,
              textAlign: 'center',
              mb: 1,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box mb={2}>
              <PaymentIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Fund Your Account
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Choose your preferred deposit method and amount
            </Typography>

            {/* Decorative Wave */}
            <Box
              component="img"
              src="/waves.svg"
              alt=""
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                pointerEvents: 'none',
                opacity: 0.4,
              }}
            />
          </Paper>
        </motion.div>

        {/* Method & Amount Form */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            mb: 10,
            p: { xs: 2, md: 4 },
            maxWidth: 520,
            mx: 'auto',
          }}
        >
          {/* Deposit Methods */}
          <RadioGroup value={method} onChange={(e) => setMethod(e.target.value)} sx={{ mb: 3 }}>
            {depositMethods.map((item) => (
              <Paper
                key={item.value}
                variant="outlined"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 2,
                  borderColor: method === item.value ? '#0ea5e9' : 'divider',
                  bgcolor: method === item.value ? '#f0f9ff' : 'background.paper',
                  transition: '0.2s',
                }}
              >
                <Box mr={2}>{item.icon}</Box>
                <Typography flex={1}>{item.label}</Typography>
                <Radio value={item.value} />
              </Paper>
            ))}
          </RadioGroup>

          <Divider sx={{ my: 3 }} />

          {/* Amount Input */}
          <Box mb={3}>
            <Typography variant="body2" fontWeight={500} mb={1}>
              Deposit Amount
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #0ea5e9',
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              <Typography fontWeight={600} mr={1}>
                $
              </Typography>
              <TextField
                variant="standard"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '1.2rem', fontWeight: 700 },
                }}
                type="number"
                sx={{ flex: 1 }}
              />
              <Typography color="text.secondary" fontWeight={600}>
                .00
              </Typography>
            </Box>
          </Box>

          {/* Preset Amounts */}
          <Grid container spacing={1.5} mb={3}>
            {presetAmounts.map((amt) => (
              <Grid item xs={4} sm={4} key={amt}>
                <Button
                  onClick={() => setAmount(amt)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    fontSize: '0.8rem',
                    borderRadius: 2,
                    borderColor: amount == amt ? '#0ea5e9' : 'divider',
                    bgcolor: amount == amt ? '#e0f2fe' : 'transparent',
                    fontWeight: 500,
                  }}
                >
                  ${amt}
                </Button>
              </Grid>
            ))}
          </Grid>

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ py: 1.2, borderRadius: 2 }}
            startIcon={<PaymentIcon />}
            onClick={handleProceed}
          >
            Proceed to Deposit
          </Button>
        </Paper>
      </Box>
      <StickyFooter />

      {/* Modal */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={1}>
          <Typography variant="h6" fontWeight={600}>
            {method === 'bank'
              ? 'Bank Transfer Details'
              : method === 'credit'
              ? 'Credit/Debit Card'
              : 'Bitcoin Deposit'}
          </Typography>
          <IconButton onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          {renderMethodDetails()}
          <Box textAlign="center" mt={3} mb={2}>
            <Typography variant="body2" color="error" fontWeight={600}>
              Complete transaction within: {formatTime(timer)}
            </Typography>
          </Box>

          {/* Dotted caution box */}
          <Box
            sx={{
              border: '2px dotted rgba(0,0,0,0.3)',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              mt: 3,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="body2" mb={2} fontWeight={500}>
              After making payment, please confirm below:
            </Typography>
            <Button
              variant="contained"
              color="success"
              sx={{ borderRadius: 2, px: 3 }}
            >
              I Have Made the Transaction
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DepositFunds;